/**
 * Copyright 2020 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: LicenseRef-.amazon.com.-AmznSL-1.0
 * Licensed under the Amazon Software License  http://aws.amazon.com/asl/
 */

import React from 'react';
import { inject, observer } from 'mobx-react';
import { makeObservable, observable, action, runInAction } from 'mobx';
import { Button, Dimmer, Header, List, Loader, Dropdown, Segment } from 'semantic-ui-react';
import _ from 'lodash';

import { getAddUserRoleFormFields } from '../../models/forms/AddUserRoleForm';
import { displayError } from '../../helpers/notification';
import { withRouter } from '../../helpers/routing';
import validate from '../../models/forms/Validate';

class AddUserRole extends React.Component {
  formProcessing = false;
  validationErrors = new Map();
  userRole = {};

  constructor(props) {
    super(props);
    makeObservable(this, {
      formProcessing: observable,
      userRole: observable,
      validationErrors: observable,
    });

    this.state = {
      // eslint-disable-next-line react/no-unused-state
      role: 'guest',
      // eslint-disable-next-line react/no-unused-state
      status: 'active',
      userRoleType: 'INTERNAL',
    };

    // this.form = getAddUserRoleForm();
    this.addUserRoleFormFields = getAddUserRoleFormFields();
  }

  render() {
    return (
      <div className="mt2 animated fadeIn">
        <Header as="h2" icon textAlign="center" className="mt3" color="grey">
          Add User Role
        </Header>
        <div className="mt3 ml3 mr3 animated fadeIn">{this.renderAddUserRoleForm()}</div>
      </div>
    );
  }

  // eslint-disable-next-line react/no-unused-state
  // handleRoleChange = (e, { value }) => this.setState({ role: value });

  // eslint-disable-next-line react/no-unused-state
  // handleStatusChange = (e, { value }) => this.setState({ status: value });

  renderAddUserRoleForm() {
    const processing = this.formProcessing;
    const fields = this.addUserRoleFormFields;
    const toEditableInput = (attributeName, type = 'text') => {
      const handleChange = action(event => {
        event.preventDefault();
        this.userRole[attributeName] = event.target.value;
      });
      return (
        <div className="ui focus input">
          <input
            type={type}
            defaultValue={this.userRole[attributeName]}
            placeholder={fields[attributeName].placeholder || ''}
            onChange={handleChange}
          />
        </div>
      );
    };

    return (
      <Segment basic className="ui fluid form">
        <Dimmer active={processing} inverted>
          <Loader inverted>Checking</Loader>
        </Dimmer>
        {this.renderField('id', toEditableInput('id', 'id'))}
        <div className="mb4" />
        {this.renderField('description', toEditableInput('description', 'description'))}
        <div className="mb4" />
        {this.renderField('type')}
        {this.renderUserTypeSelection()}
        {this.renderButtons()}
      </Segment>
    );
  }

  renderUserTypeSelection() {
    const userRoleTypeOption = [
      {
        key: 'INTERNAL',
        value: 'INTERNAL',
        text: 'INTERNAL',
      },
      {
        key: 'EXTERNAL',
        value: 'EXTERNAL',
        text: 'EXTERNAL',
      },
    ];
    return (
      <Dropdown
        options={userRoleTypeOption}
        placeholder="Please select user role type"
        fluid
        selection
        defaultValue={userRoleTypeOption[0].value}
        onChange={this.handleUserRoleSelection}
      />
    );
  }

  handleUserRoleSelection = (e, { value }) => this.setState({ userRoleType: value });

  renderButtons() {
    const processing = this.formProcessing;
    return (
      <div className="mt3">
        <Button floated="right" color="blue" icon disabled={processing} className="ml2" onClick={this.handleSubmit}>
          Add User Role
        </Button>
        <Button floated="right" disabled={processing} onClick={this.handleCancel}>
          Cancel
        </Button>
      </div>
    );
  }

  renderField(name, component) {
    const fields = this.addUserRoleFormFields;
    const explain = fields[name].explain;
    const label = fields[name].label;
    const hasExplain = !_.isEmpty(explain);
    const fieldErrors = this.validationErrors.get(name);
    const hasError = !_.isEmpty(fieldErrors);

    return (
      <div>
        <Header className="mr3 mt0" as="h2" color="grey">
          {label}
        </Header>
        {hasExplain && <div className="mb2">{explain}</div>}
        <div className={`ui big field input block m0 ${hasError ? 'error' : ''}`}>{component}</div>
        {hasError && (
          <div className="ui pointing red basic label">
            <List>
              {_.map(fieldErrors, fieldError => (
                <List.Item key={name}>
                  <List.Content>{fieldError}</List.Content>
                </List.Item>
              ))}
            </List>
          </div>
        )}
      </div>
    );
  }

  goto(pathname) {
    // const location = this.props.location;
    // const link = createLink({ location, pathname });
    this.props.navigate(pathname);
  }

  handleCancel = action(event => {
    event.preventDefault();
    event.stopPropagation();
    this.formProcessing = false;
    this.goto('/users');
  });

  handleSubmit = action(async () => {
    this.formProcessing = true;
    try {
      // Perform client side validations first
      const validationResult = await validate(this.userRole, this.addUserRoleFormFields);
      // if there are any client side validation errors then do not attempt to make API call
      if (validationResult.fails()) {
        runInAction(() => {
          this.validationErrors = validationResult.errors;
          this.formProcessing = false;
        });
      } else {
        // There are no client side validation errors so ask the store to add user (which will make API call to server to add the user)
        // this.user.userRole = this.state.role;
        // this.user.status = this.state.status;
        // this.user.authenticationProviderId = this.state.identityProviderName;
        // await this.getStore().addUser(this.userRole);
        this.userRole.userType = this.state.userRoleType;
        await this.props.userRolesStore.addUserRole(this.userRole);
        runInAction(() => {
          this.formProcessing = false;
        });
        this.goto('/users');
      }
    } catch (error) {
      runInAction(() => {
        this.formProcessing = false;
      });
      displayError(error);
    }
  });

  /* getStore() {
    return this.props.usersStore;
  } */
}

export default inject('usersStore', 'userRolesStore')(withRouter(observer(AddUserRole)));
